/**
 * THE CYCLE RING — the signature element.
 *
 * A statement cycle is a closed loop, so it is drawn as one. The ring runs
 * clockwise from 12 o'clock: cycle start → statement close (a tick on the arc)
 * → due date (the terminal marker). The filled arc is how far through that loop
 * today sits, and its color shifts cyan → amber → rose as the due date nears.
 *
 * Forty of these read as a constellation you can scan without reading a single
 * number — which is the point. Everything else on the page stays quiet.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { usePrefersReducedMotion } from '@/lib/motion'
import { daysFromToday, formatDate, formatDueDate, parseDate, type ApiDate } from '@/lib/format'
import { cn } from '@/lib/utils'

export type CycleRingSize = 'sm' | 'md' | 'lg'

/** Which band the account is in. Exported so rows can label it in words too. */
export type CycleUrgency = 'paid' | 'comfortable' | 'soon' | 'urgent' | 'overdue' | 'unknown'

export interface CycleRingProps {
  /** Statement cycle open date. */
  cycleStart?: ApiDate
  /** Statement close date. Drawn as a tick on the arc. */
  cycleEnd?: ApiDate
  /** Payment due date — the marker the whole ring is about. */
  dueDate?: ApiDate
  /** The obligation is settled: the ring goes mint and stops warning. */
  paid?: boolean
  size?: CycleRingSize
  /** Figure at the center — usually a compact `<Money>`. Hidden at `sm`. */
  children?: ReactNode
  /** Small caption under the center figure, e.g. "•••• 4821". Hidden at `sm`. */
  caption?: string
  /** Reference date. Injectable for tests and for the dashboard's `today`. */
  today?: Date
  /** Names the ring for assistive tech. A sensible sentence is generated if omitted. */
  label?: string
  className?: string
}

interface Geometry {
  box: number
  radius: number
  stroke: number
  markerRadius: number
}

const GEOMETRY: Record<CycleRingSize, Geometry> = {
  sm: { box: 36, radius: 14, stroke: 3, markerRadius: 2.5 },
  md: { box: 60, radius: 24, stroke: 4, markerRadius: 3.5 },
  lg: { box: 104, radius: 43, stroke: 6, markerRadius: 5 },
}

const URGENCY_STROKE: Record<CycleUrgency, string> = {
  paid: 'var(--mint)',
  comfortable: 'var(--cyan)',
  soon: 'var(--amber)',
  urgent: 'var(--rose)',
  overdue: 'var(--rose)',
  unknown: 'var(--text-dim)',
}

/**
 * Where today sits in the cycle, and how worried to be about it.
 * Exported because rows and tiles need the same banding as the ring, and two
 * implementations of "is this urgent" would eventually disagree.
 */
export function computeCycle(props: {
  cycleStart?: ApiDate
  cycleEnd?: ApiDate
  dueDate?: ApiDate
  paid?: boolean
  today?: Date
}): {
  urgency: CycleUrgency
  /** 0–1 through the loop, or null when there is no cycle to be in. */
  progress: number | null
  /** 0–1 position of the statement close tick, or null. */
  closePosition: number | null
  daysUntilDue: number | null
  hasCycle: boolean
} {
  const today = props.today ?? new Date()
  const start = parseDate(props.cycleStart)
  const end = parseDate(props.cycleEnd)
  const due = parseDate(props.dueDate)
  const daysUntilDue = daysFromToday(props.dueDate, today)

  // The loop spans cycle start → due date. Falling back to the close date keeps
  // charge cards and odd issuers drawable; with neither, there is no ring.
  const from = start ?? end
  const to = due ?? end
  const hasCycle = Boolean(from && to && to.getTime() > from.getTime())

  if (!hasCycle) {
    return {
      urgency: props.paid ? 'paid' : daysUntilDue === null ? 'unknown' : bandFor(daysUntilDue),
      progress: null,
      closePosition: null,
      daysUntilDue,
      hasCycle: false,
    }
  }

  const span = to!.getTime() - from!.getTime()
  const progress = clamp01((today.getTime() - from!.getTime()) / span)
  const closePosition = end ? clamp01((end.getTime() - from!.getTime()) / span) : null

  return {
    urgency: props.paid ? 'paid' : daysUntilDue === null ? 'comfortable' : bandFor(daysUntilDue),
    progress,
    closePosition,
    daysUntilDue,
    hasCycle: true,
  }
}

function bandFor(days: number): CycleUrgency {
  if (days < 0) return 'overdue'
  if (days <= 2) return 'urgent'
  if (days <= 7) return 'soon'
  return 'comfortable'
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

export function CycleRing({
  cycleStart,
  cycleEnd,
  dueDate,
  paid = false,
  size = 'md',
  children,
  caption,
  today,
  label,
  className,
}: CycleRingProps) {
  const { box, radius, stroke, markerRadius } = GEOMETRY[size]
  const { urgency, progress, closePosition, daysUntilDue, hasCycle } = computeCycle({
    cycleStart,
    cycleEnd,
    dueDate,
    paid,
    today,
  })

  const prefersReducedMotion = usePrefersReducedMotion()
  const circumference = 2 * Math.PI * radius
  const target = (progress ?? 0) * circumference

  // Draw once on mount. The arc grows from nothing to its length; after that
  // it only ever jumps to a new value, which is honest — the data changed.
  const [drawn, setDrawn] = useState(prefersReducedMotion)
  useEffect(() => {
    if (prefersReducedMotion) {
      setDrawn(true)
      return
    }
    const frame = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(frame)
  }, [prefersReducedMotion])

  const center = box / 2
  const color = URGENCY_STROKE[urgency]
  const description = label ?? describe({ hasCycle, daysUntilDue, dueDate, cycleEnd, paid })

  return (
    <div
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: box, height: box }}
    >
      <svg
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        role="img"
        aria-label={description}
        // An SVG arc starts at 3 o'clock; the rotation puts the start of the
        // cycle at 12 o'clock, so the loop reads clockwise like a clock face.
        className="-rotate-90"
      >
        {/* The loop itself. Dashed when there is no cycle to draw. */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--hairline)"
          strokeWidth={stroke}
          strokeDasharray={hasCycle ? undefined : '2 4'}
        />

        {hasCycle && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - (drawn ? target : 0)}
            style={{
              transition: prefersReducedMotion
                ? undefined
                : 'stroke-dashoffset 700ms cubic-bezier(0.2, 0.7, 0.2, 1)',
            }}
          />
        )}

        {/* Statement close: the moment the balance froze. */}
        {hasCycle && closePosition !== null && closePosition > 0 && closePosition < 1 && (
          <line
            x1={center + (radius - stroke) * Math.cos(closePosition * 2 * Math.PI)}
            y1={center + (radius - stroke) * Math.sin(closePosition * 2 * Math.PI)}
            x2={center + (radius + stroke) * Math.cos(closePosition * 2 * Math.PI)}
            y2={center + (radius + stroke) * Math.sin(closePosition * 2 * Math.PI)}
            stroke="var(--text-dim)"
            strokeWidth={1.5}
          />
        )}

        {/* The due date closes the loop — drawn where it started, at the top. */}
        {hasCycle && (
          <circle
            cx={center + radius}
            cy={center}
            r={markerRadius}
            fill={urgency === 'paid' ? 'var(--mint)' : color}
            stroke="var(--panel)"
            strokeWidth={1.5}
          />
        )}
      </svg>

      {size !== 'sm' && (children || caption) && (
        <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center leading-none">
          {children}
          {caption && (
            <span className="tabular text-dim text-[0.625rem] leading-none">{caption}</span>
          )}
        </span>
      )}
    </div>
  )
}

function describe({
  hasCycle,
  daysUntilDue,
  dueDate,
  cycleEnd,
  paid,
}: {
  hasCycle: boolean
  daysUntilDue: number | null
  dueDate?: ApiDate
  cycleEnd?: ApiDate
  paid: boolean
}): string {
  if (paid) return `Paid. ${dueDate ? `Was due ${formatDate(dueDate)}.` : ''}`.trim()
  if (daysUntilDue !== null) {
    const closed = cycleEnd ? ` Statement closed ${formatDate(cycleEnd)}.` : ''
    return `${formatDueDate(dueDate)}.${closed}`
  }
  return hasCycle ? 'Statement cycle in progress. No due date.' : 'No statement cycle for this account.'
}
